import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Card, Typography, Space, Row,Col,Tag } from 'antd';
import { UserAddOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';

const { Title, Text } = Typography;

const Users = () => {
    const [employees, setEmployees] = useState([]);
    const [shops, setShops] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);

    // Fetching employees, shops, and roles when the component mounts
    useEffect(() => {
        fetchEmployees();
    }, []);

    // Fetch employees, shops, and roles from the backend
    const fetchEmployees = async () => {
        setTableLoading(true);
        try {
            const response = await axiosInstance.get('/employees');
            const { employees, shops = [], roles = [] } = response.data;
            setEmployees(employees);
            setShops(shops);
            setRoles(roles);
        } catch (error) {
            message.error('Failed to fetch employees.');
        } finally {
            setTableLoading(false);
        }
    };

    // Show modal to add or edit an employee
    const showModal = (employee = null) => {
        setEditingEmployee(employee);
        if (employee) {
            form.setFieldsValue({
                name: employee.user.name,
                email: employee.user.email,
                phone: employee.user.phone,
                role_id: employee.user.role_id,
                shop_ids: employee.shop_id ? [employee.shop_id] : [],
            });
        }
        setIsModalVisible(true);
    };

    // Handle save (either add or update employee)
    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingEmployee) {
                await axiosInstance.put(`/employees/${editingEmployee.user.id}`, values);
                message.success('Employee updated successfully');
            } else {
                await axiosInstance.post('/register', values);
                message.success('Employee added successfully');
            }

            setIsModalVisible(false);
            form.resetFields();
            fetchEmployees();
        } catch (error) {
            console.error('Error saving employee:', error);
            message.error(error.response?.data?.message || 'Failed to save employee.');
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel button in modal
    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingEmployee(null);
    };

    // Table columns for displaying employee data
    const columns = [
        { 
            title: 'Name', 
            dataIndex: ['user', 'name'], 
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        { 
            title: 'Email', 
            dataIndex: ['user', 'email'], 
            key: 'email',
            responsive: ['md']
        },
        { 
            title: 'Phone', 
            dataIndex: ['user', 'phone'], 
            key: 'phone',
            responsive: ['md']
        },
        { 
            title: 'Shop', 
            dataIndex: ['shop', 'name'], 
            key: 'shop',
            render: (text) => text ? <Tag color="blue">{text}</Tag> : '-'
        },
        { 
            title: 'Role', 
            dataIndex: ['user', 'role', 'name'],
            key: 'role',
            render: (text) => text ? <Tag color="geekblue">{text}</Tag> : '-',
            responsive: ['lg']
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />} 
                        onClick={() => showModal(record)}
                    />
                    <Button 
                        size="small" 
                        icon={<DeleteOutlined />} 
                        danger
                        // Add your delete functionality here
                    />
                </Space>
            ),
        },
    ];

    return (
        <Card 
            title={<Title level={4} style={{ margin: 0 }}>User Management</Title>}
            bordered={false}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
            extra={
                <Button 
                    type="primary" 
                    icon={<UserAddOutlined />}
                    onClick={() => showModal()}
                >
                    Add User
                </Button>
            }
        >
            <Table
                columns={columns}
                dataSource={employees}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showTotal: (total) => <Text>Total {total} users</Text>
                }}
                scroll={{ x: true }}
                rowKey={record => record.id}
                loading={tableLoading}
                size="middle"
                bordered
                style={{ marginTop: 16 }}
            />

            <Modal
                title={<Title level={5} style={{ margin: 0 }}>{editingEmployee ? "Edit User" : "Add New User"}</Title>}
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                okText={editingEmployee ? "Update" : "Add"}
                cancelText="Cancel"
                confirmLoading={loading}
                destroyOnClose
                width={700}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item 
                                name="name" 
                                label="Full Name" 
                                rules={[{ required: true, message: 'Please input the name!' }]}
                            >
                                <Input placeholder="Enter full name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item 
                                name="email" 
                                label="Email" 
                                rules={[{ 
                                    required: true, 
                                    type: 'email', 
                                    message: 'Please enter a valid email!' 
                                }]}
                            >
                                <Input placeholder="Enter email address" />
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item 
                                name="phone" 
                                label="Phone Number" 
                                rules={[{ required: true, message: 'Please input the phone number!' }]}
                            >
                                <Input placeholder="Enter phone number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item 
                                name="role_id" 
                                label="Role" 
                                rules={[{ required: true, message: 'Please select a role!' }]}
                            >
                                <Select 
                                    placeholder="Select role"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {roles.map(role => (
                                        <Select.Option key={role.id} value={role.id}>
                                            {role.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Form.Item 
                        name="shop_ids" 
                        label="Assigned Shops" 
                        rules={[{ required: true, message: 'Please select at least one shop!' }]}
                    >
                        <Select 
                            mode="multiple" 
                            placeholder="Select shops"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {shops.map(shop => (
                                <Select.Option key={shop.id} value={shop.id}>
                                    {shop.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Users;